import {
  reorderNurseSequence,
  type NurseSequenceReorderRequest,
} from "@/api/nurses";
import { z } from "zod";

const PENDING_SAVE_KEY_PREFIX = "roster-nurse-sequence-pending";
const saveQueueByGroup = new Map<string, Promise<void>>();
const replayByGroup = new Map<string, Promise<boolean>>();

const pendingSaveSchema = z.object({
  payload: z.object({
    active_order: z.array(z.string()),
    inactive_order: z.array(z.string()),
  }),
});

class RosterNurseSequenceLifecycleSaveError extends Error {
  readonly status: number;

  constructor(status: number) {
    super("Nurse sequence lifecycle save failed");
    this.name = "RosterNurseSequenceLifecycleSaveError";
    this.status = status;
  }
}

function queueKey(groupId?: string | null): string {
  return groupId?.trim() ?? "";
}

function storageKey(groupId?: string | null): string {
  return `${PENDING_SAVE_KEY_PREFIX}:${encodeURIComponent(queueKey(groupId))}`;
}

function pendingSaveStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    if (error instanceof DOMException) return null;
    throw error;
  }
}

function removePersistedSave(groupId?: string | null): void {
  const storage = pendingSaveStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(groupId));
  } catch (error) {
    if (error instanceof DOMException) return;
    throw error;
  }
}

function payloadMatches(
  left: NurseSequenceReorderRequest,
  right: NurseSequenceReorderRequest
): boolean {
  return (
    left.active_order.join("\u0000") === right.active_order.join("\u0000") &&
    left.inactive_order.join("\u0000") === right.inactive_order.join("\u0000")
  );
}

export function persistRosterNurseSequenceSave(
  payload: NurseSequenceReorderRequest,
  groupId?: string | null
): void {
  const storage = pendingSaveStorage();
  if (!storage) return;
  try {
    storage.setItem(storageKey(groupId), JSON.stringify({ payload }));
  } catch (error) {
    if (error instanceof DOMException) return;
    throw error;
  }
}

function readPersistedSave(
  groupId?: string | null
): NurseSequenceReorderRequest | null {
  const storage = pendingSaveStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey(groupId));
    if (!raw) return null;
    const parsed = pendingSaveSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data.payload;
    removePersistedSave(groupId);
    return null;
  } catch (error) {
    if (error instanceof SyntaxError) {
      removePersistedSave(groupId);
      return null;
    }
    if (error instanceof DOMException) return null;
    throw error;
  }
}

function clearPersistedSaveIfMatches(
  payload: NurseSequenceReorderRequest,
  groupId?: string | null
): void {
  const stored = readPersistedSave(groupId);
  if (!stored || !payloadMatches(stored, payload)) return;
  removePersistedSave(groupId);
}

function trackSave(key: string, request: Promise<void>): Promise<void> {
  const tracked = request.finally(() => {
    if (saveQueueByGroup.get(key) === tracked) saveQueueByGroup.delete(key);
  });
  saveQueueByGroup.set(key, tracked);
  return tracked;
}

export function enqueueRosterNurseSequenceSave(
  payload: NurseSequenceReorderRequest,
  groupId?: string | null
): Promise<void> {
  persistRosterNurseSequenceSave(payload, groupId);
  const key = queueKey(groupId);
  const previous = saveQueueByGroup.get(key) ?? Promise.resolve();
  const request = previous
    .catch(() => undefined)
    .then(async () => {
      await reorderNurseSequence(payload, groupId);
      clearPersistedSaveIfMatches(payload, groupId);
    });
  return trackSave(key, request);
}

function lifecycleSaveUrl(groupId?: string | null): string {
  const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
  const path = `${baseUrl}/nurses/sequence/reorder`;
  const group = groupId?.trim();
  return group ? `${path}?group_id=${encodeURIComponent(group)}` : path;
}

export function flushRosterNurseSequenceOnPageHide(
  payload: NurseSequenceReorderRequest,
  groupId?: string | null
): Promise<void> | null {
  persistRosterNurseSequenceSave(payload, groupId);
  const key = queueKey(groupId);
  if (saveQueueByGroup.has(key)) return null;

  const request = fetch(lifecycleSaveUrl(groupId), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(async (response) => {
    if (!response.ok) {
      throw new RosterNurseSequenceLifecycleSaveError(response.status);
    }
    clearPersistedSaveIfMatches(payload, groupId);
  });
  return trackSave(key, request);
}

export function replayPersistedRosterNurseSequenceSave(
  groupId?: string | null
): Promise<boolean> {
  const key = queueKey(groupId);
  const currentReplay = replayByGroup.get(key);
  if (currentReplay) return currentReplay;
  const payload = readPersistedSave(groupId);
  if (!payload) return Promise.resolve(false);

  const replay = enqueueRosterNurseSequenceSave(payload, groupId)
    .then(() => true)
    .finally(() => {
      if (replayByGroup.get(key) === replay) replayByGroup.delete(key);
    });
  replayByGroup.set(key, replay);
  return replay;
}

export async function waitForRosterNurseSequenceQueue(
  groupId?: string | null
): Promise<void> {
  const key = queueKey(groupId);
  while (saveQueueByGroup.has(key)) {
    const current = saveQueueByGroup.get(key);
    await current?.catch(() => undefined);
  }
}
